import { useMutation, useQuery } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import routing from "routing";
import newApi from "src/newApi";

import { isLek } from "../helpers/services";
import { queryClient } from "src/queryClient";
import options, { redirectToAuth } from "./defaultOptions";

export function useGetSpecialisations(enabled = true) {
	return useQuery({
		queryKey: ["specialisations"],

		queryFn: () => {
			if (!isLek()) {
				return [];
			}

			return newApi.getSpecialisations();
		},

		...options,
		enabled,
	});
}

export function useUploadAvatar() {
	return useMutation({
		mutationFn: async ({ file }) => {
			const avatar = await newApi.uploadImage(file);
			return avatar.url;
		},
	});
}

export function useGetUniversities() {
	return useQuery({
		queryKey: ["universities"],
		queryFn: () => newApi.getUniversities(),
		...options,
	});
}

export function useGetUserInfo(enabled = true) {
	return useQuery({
		queryKey: ["user-info"],
		queryFn: () => newApi.getMe(),
		...options,
		enabled,
		staleTime: 60 * 1000,
	});
}

export function getUserInfo() {
	return queryClient.getQueryData(["user-info"]) || {};
}

export function getUserSpecialisationName() {
	const user = getUserInfo();

	if (!user.selectedSpecialisation) {
		return null;
	}

	return user.selectedSpecialisation.specialisation;
}

export function getUserSpecialisationId() {
	const user = getUserInfo();

	if (!user.selectedSpecialisation) {
		return null;
	}

	return user.selectedSpecialisation.id;
}

export function isUserBanned() {
	const user = getUserInfo();

	return user.isBanned;
}

export function isStaff() {
	const user = getUserInfo();

	return user.isStaff;
}

export function getUserId() {
	const user = getUserInfo();

	return user.id;
}

export function getUserShort() {
	const user = getUserInfo();
	return {
		id: user.id,
		username: user.username,
		isStaff: user.isStaff,
		isBanned: user.isBanned,
	};
}

export function useLogout() {
	return useMutation({
		mutationFn: async () => {
			await newApi.logout();
			location.href = generatePath(routing.paths.signIn);
		},
	});
}

export function useRemoveUser() {
	return useMutation({
		mutationFn: async () => {
			await newApi.deleteAccount();
			await newApi.logout();
			await redirectToAuth();
		},
	});
}

export function useActivePromocode() {
	return useMutation({
		mutationFn: async ({ promocode }) => {
			await newApi.activateUserPromocode(promocode);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user-info"] });
		},
	});
}

export function useSetSpecialisation() {
	return useMutation({
		mutationFn: (data) => {
			return newApi.updateMe({ specialization: data.id });
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["user-info"] }),
				queryClient.invalidateQueries({ queryKey: ["app-config"] }),
				queryClient.invalidateQueries({ queryKey: ["comments"] }),
			]);
			location.reload();
		},
	});
}

// id z tabeli Service w Django (PATCH /api/v3/me/ service_id)
const SERVICE_IDS = { LEK: 1, PES: 2 };

export function useSetService() {
	return useMutation({
		mutationFn: (service) => {
			return newApi.updateMe({ serviceId: SERVICE_IDS[service] });
		},
		onSuccess: () => {
			location.reload();
		},
	});
}

export function useEditUser() {
	return useMutation({
		mutationFn: (data) => {
			return newApi.updateMe(data);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["user-info"] }),
			]);
		},
	});
}
